/* assets/src/viewer/furniture/manager.js
 * STEP B — Arredi GLB CC0 (Poly Haven). Caricamento LAZY per preset, decoder meshopt
 * BUNDLATO (zero file extra), texture WebP decodificate dal browser (zero transcoder).
 * Cloni che condividono geometria/materiale, ombre, LOD distance-cull, posizionamento
 * relativo all'ingombro della pergola. Licenze: assets/models/furniture/LICENSES.md.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

const PRESETS = {
  terrazza: ['dining'],
  giardino: ['dining', 'plantCorner'],
  piscina: ['lounge', 'plantCorner'],
};

export class FurnitureManager {
  constructor(viewer) {
    this.viewer = viewer;
    this.base = (window.ASG_Data && window.ASG_Data.furnitureBase) || 'assets/models/furniture/';
    this.loader = new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.cache = new Map();
    this.preset = 'terrazza';
    this._sig = null;

    this.group = new THREE.Group();
    this.group.name = 'asg-furniture';
    this.group.visible = false;
    viewer.scene.add(this.group);
    window.furnitureGroup = this.group;
  }

  _proto(name) {
    if (!this.cache.has(name)) {
      this.cache.set(name, new Promise((resolve, reject) => {
        this.loader.load(this.base + name + '.glb', (gltf) => {
          const root = gltf.scene;
          root.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; if (o.material) o.material.envMapIntensity = 0.6; } });
          resolve(root);
        }, undefined, reject);
      }));
    }
    return this.cache.get(name);
  }

  async _instance(name) {
    const proto = await this._proto(name);
    return proto.clone(true); // condivide geometrie/materiali
  }

  // LOD: oltre `far` l'arredo viene nascosto (cull economico per oggetti lontani).
  _lod(obj, far = 26) {
    const lod = new THREE.LOD();
    lod.addLevel(obj, 0);
    lod.addLevel(new THREE.Group(), far);
    return lod;
  }

  async setPreset(preset) {
    this.preset = preset && PRESETS[preset] ? preset : 'terrazza';
    if (this.group.visible) { this._sig = null; await this._build(); }
  }

  async setVisible(v) {
    this.group.visible = !!v;
    if (v) await this._build();
    this.viewer.requestRender();
  }

  // Chiamato su resize della pergola: riposiziona se visibile, ma DEBOUNCED (~250ms) così durante
  // il drag degli slider gli arredi NON si ricostruiscono ad ogni step (niente flicker).
  onSceneResized() {
    if (!this.group.visible) return;
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => { this._resizeTimer = 0; this._build(); }, 250);
  }

  async _build() {
    // Serializza: evita build concorrenti (es. ToggleFurniture + setEnvironmentPreset) che
    // si interleaverebbero aggiungendo arredi duplicati.
    if (this._building) { this._pending = true; return; }
    this._building = true;
    try { await this._buildOnce(); } finally { this._building = false; }
    if (this._pending) { this._pending = false; this._sig = null; await this._build(); }
  }

  async _buildOnce() {
    let w = 3; let d = 2.5; let cx = 0; let cz = 0;
    const box = this.viewer.currentSceneBounds;
    if (box && !box.isEmpty()) {
      const s = box.getSize(new THREE.Vector3()); const c = box.getCenter(new THREE.Vector3());
      w = s.x; d = s.z; cx = c.x; cz = c.z;
    }
    const sig = `${this.preset}|${Math.round(w * 10)}|${Math.round(d * 10)}|${Math.round(cx * 10)}|${Math.round(cz * 10)}`;
    if (sig === this._sig) return;
    this._sig = sig;
    while (this.group.children.length) this.group.remove(this.group.children[0]);

    const sets = PRESETS[this.preset] || PRESETS.terrazza;
    try {
      if (sets.includes('dining')) await this._dining(cx, cz, w, d);
      if (sets.includes('lounge')) await this._lounge(cx, cz, w, d);
      if (sets.includes('plantCorner')) await this._plantCorner(cx, cz, w, d);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[ASG Viewer F6] arredi: caricamento fallito', e);
    }
    this.viewer.requestRender();
  }

  async _place(name, x, z, ry = 0, scale = 1) {
    const obj = await this._instance(name);
    obj.position.set(x, 0, z); obj.rotation.y = ry; obj.scale.setScalar(scale);
    this.group.add(this._lod(obj));
  }

  async _dining(cx, cz, w, d) {
    // Set bistro outdoor completo (tavolo + sedie in un unico modello), centrato.
    await this._place('diningset', cx, cz, 0);
  }

  async _lounge(cx, cz, w, d) {
    // Tavolino basso + 2 poltrone moderne che lo guardano.
    await this._place('cofftable', cx, cz, 0);
    const off = Math.min(1.15, Math.max(0.7, Math.min(w, d) * 0.3));
    await Promise.all([
      this._place('armchair', cx - off, cz + off * 0.2, Math.PI * 0.55),
      this._place('armchair', cx + off, cz + off * 0.2, -Math.PI * 0.55),
    ]);
  }

  async _plantCorner(cx, cz, w, d) {
    await this._place('plant', cx + w * 0.4, cz - d * 0.4, 0);
  }
}
